<template>
  <MetaInfo 
    title="Start Your SaaS Journey | Transpond" 
    description="Launch your SaaS business with Transpond's powerful email marketing automation. Get started for free today."
  />

  <div class="saas-rise-page">
    <!-- Header with Logo Only -->
    <header class="saas-rise-header">
      <div class="container">
        <div class="header-content">
          <LogoFull class="saas-rise-logo" />
          <div class="saas-rise-badge">
            <a 
              href="https://www.saasrise.com/?utm_source=CapsuleCRM" 
              target="_blank" 
              rel="noopener noreferrer"
              class="saas-rise-logo-container"
            >
              <svg class="saas-rise-logo-svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <defs>
                  <linearGradient id="saasrise-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:#FFD700;stop-opacity:1" />
                    <stop offset="50%" style="stop-color:#FF6B6B;stop-opacity:1" />
                    <stop offset="100%" style="stop-color:#6B73FF;stop-opacity:1" />
                  </linearGradient>
                </defs>
                <circle cx="10" cy="10" r="8" fill="url(#saasrise-gradient)"/>
                <circle cx="10" cy="10" r="3" fill="white"/>
              </svg>
              <span class="saas-rise-text">Used by SaasRise</span>
            </a>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="saas-rise-main">
      <div class="container">
        <div class="saas-rise-content">
          <!-- Left Side - Upsell Content -->
          <div class="saas-rise-upsell">
            <h1 class="saas-rise-title">
              Launch your business with SaasRise partner Transpond.<br>
              <span class="highlight">Rise Above the Competition</span>
            </h1>
            
            <p class="saas-rise-subtitle">
              Transform your business with Transpond’s powerful marketing automation platform.
            </p>

            <div class="saas-rise-features">
              <div class="feature-item">
                <div class="feature-content">
                  <h3>Beautiful Emails & AI Content Creation</h3>
                  <p>Design professional emails with drag & drop and let AI write compelling content for you instantly</p>
                </div>
              </div>
              
              <div class="feature-item">
                <div class="feature-content">
                  <h3>Grow Your Customer List</h3>
                  <p>Easily collect email addresses from your website visitors and turn them into customers</p>
                </div>
              </div>
              
              <div class="feature-item">
                <div class="feature-content">
                  <h3>Set It and Forget It Marketing</h3>
                  <p>Automate emails and social media posts across all platforms - while you focus on your business</p>
                </div>
              </div>
              
              <div class="feature-item">
                <div class="feature-content">
                  <h3>See What's Actually Working</h3>
                  <p>Simple reports show you which emails get opened and what brings customers to your site</p>
                </div>
              </div>
              
              <div class="feature-item">
                <div class="feature-content">
                  <h3>Talk to Customers Instantly</h3>
                  <p>Answer questions and help customers right on your website with built-in chat</p>
                </div>
              </div>
            </div>

            <!-- hide stats for now <div class="saas-rise-stats">
              <div class="stat">
                <strong>10,000+</strong>
                <span>SaaS companies trust us</span>
              </div>
              <div class="stat">
                <strong>99.9%</strong>
                <span>Uptime guarantee</span>
              </div>
              <div class="stat">
                <strong>24/7</strong>
                <span>Expert support</span>
              </div>
            </div> -->
          </div>

          <!-- Right Side - Signup Form -->
          <div class="saas-rise-signup">
            

            <div class="signup-card">
              <div v-if="redirecting" class="redirect-overlay"></div>
              
              <!-- Success Message -->
              <div v-if="signupSuccess" class="success-message">
                <div class="success-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M9 12l2 2 4-4"/>
                  </svg>
                </div>
                <h2>Welcome to Transpond!</h2>
                <p class="success-text">
                  Your account has been created successfully. We've sent a confirmation email to <strong>{{ email }}</strong>.
                </p>
                <p class="email-reminder">
                  Please check your email inbox (and spam folder) to verify your account and get started.
                </p>
                <a href="https://app.transpond.io/" class="login-btn">
                  Go to Login
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </a>
              </div>
              
              <!-- Signup Form -->
              <div v-else>
                <h2>Create your free account</h2>

                <p class="trusted-text"><span class="trusted-by-saasrise">Trusted by SaasRise</span> - Powerful marketing made easy,<br>no credit card required.</p>

                
                <form class="signup-form" data-gtm-context="SaasRise" @submit.prevent="handleSignup">
                <div class="form-group">
                  <label for="company">Company Name</label>
                  <input 
                    type="text" 
                    id="company" 
                    v-model="company"
                    class="form-input"
                    required
                  />
                </div>
                
                <div class="form-row">
                  <div class="form-group">
                    <label for="firstName">First Name</label>
                    <input 
                      type="text" 
                      id="firstName" 
                      v-model="firstName"
                      class="form-input"
                      required
                    />
                  </div>
                  
                  <div class="form-group">
                    <label for="lastName">Last Name</label>
                    <input 
                      type="text" 
                      id="lastName" 
                      v-model="lastName"
                      class="form-input"
                      required
                    />
                  </div>
                </div>
                
                <div class="form-group">
                  <label for="email">Email Address</label>
                  <input 
                    type="email" 
                    id="email" 
                    v-model="email"
                    class="form-input"
                    required
                  />
                </div>
                
                <div class="form-group">
                  <label for="password">Password</label>
                  <div class="password-container">
                    <input 
                      :type="showPassword ? 'text' : 'password'" 
                      id="password" 
                      v-model="password"
                      class="form-input"
                      required
                    />
                    <button 
                      type="button" 
                      class="password-toggle"
                      @click="showPassword = !showPassword"
                    >
                      <svg v-if="!showPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                      <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    </button>
                  </div>
                  <p class="password-requirements">Must contain 8 characters, 1 number, and at least 1 special character.</p>
                </div>
                
                <div class="form-checkboxes">
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="acceptTerms" required />
                    <span class="checkbox-text">
                      I accept the <a href="/terms/" target="_blank">Terms and Conditions</a> and <a href="/privacy/" target="_blank">Privacy Policy</a>
                    </span>
                  </label>
                  
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="receiveUpdates" />
                    <span class="checkbox-text">
                      I want to receive news and updates about Transpond
                    </span>
                  </label>
                </div>
                
                <button type="submit" class="create-account-btn">
                  Create Account
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </button>
                <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
              </form>

                <div class="login-link">
                  <p>Already have an account? <a href="https://app.transpond.io/">Log in</a></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import MetaInfo from "@/components/MetaInfo";
import LogoFull from "@/components/Svgs/Logo/LogoFull";
import { gtmRegister, gtmRegistrationStart } from "@/functions/gtm-tracking";

export default {
  name: "SaasRise",
  
  components: {
    MetaInfo,
    LogoFull,
  },

  data() {
    return {
      company: '',
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      showPassword: false,
      acceptTerms: false,
      receiveUpdates: false,
      errorMessage: '',
      redirecting: false,
      signupSuccess: false
    };
  },

  methods: {
    async handleSignup() {
      this.errorMessage = '';
      this.redirecting = true;
      gtmRegistrationStart("Form Submit", "Create account", "SaasRise")();
      const signupUrl = 'https://api.transpond.io/register';
      try
      {
      const response = await fetch(signupUrl, {
        method: 'POST',
        body: JSON.stringify({
          accountName: this.company,
          emailAddress: this.email,
          password: this.password,  
          firstName: this.firstName,
          lastName: this.lastName,
          url: window.location.host.split('/')[0],
          newsletter: this.receiveUpdates
        }),
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      }); 
      
      const respData = await response.json();
      if(respData.error || respData.message)
      {
        this.redirecting = false;
        this.errorMessage = respData.error || respData.message;
        return;
      }
      else
      {
        this.redirecting = false;
        this.signupSuccess = true;
        gtmRegister("Form Submit", "Create account", "SaasRise")();
      }

    }
    catch(err)
    {
     console.log("ERROR",err);
     this.redirecting = false;
     this.errorMessage = 'An unexpected error occurred. Please try again.';
    }

    }
  }
}



</script>

<style lang="scss" scoped>
.saas-rise-page {
  min-height: 100vh;
  background-color: var(--color-primary-stone);
}

.saas-rise-header {
  padding: 2rem 0;
  background: var(--color-white);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 1rem;
      text-align: center;
    }
  }
}

.saas-rise-logo {
  height: 40px;
  width: auto;
}

.saas-rise-badge {
  .saas-rise-logo-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background: linear-gradient(135deg, #FF6B6B 0%, #E85A5A 100%);
    border-radius: 2rem;
    box-shadow: 0 2px 8px rgba(232, 90, 90, 0.2);
    text-decoration: none;
    transition: all 0.3s ease;
    cursor: pointer;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(232, 90, 90, 0.3);
      background: linear-gradient(135deg, #FF7B7B 0%, #F86A6A 100%);
      
      .saas-rise-logo-svg {
        transform: scale(1.1);
      }
      
      .saas-rise-text {
        color: white;
      }
    }
    
    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 8px rgba(232, 90, 90, 0.4);
    }
    
    .saas-rise-logo-svg {
      flex-shrink: 0;
      transition: transform 0.3s ease;
    }
    
    .saas-rise-text {
      font-size: 0.875rem;
      font-weight: 600;
      color: white;
      white-space: nowrap;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      transition: color 0.3s ease;
    }
  }
}

.saas-rise-main {
  padding: 4rem 0;
  
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }
}

.saas-rise-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
}

.saas-rise-upsell {
  .saas-rise-title {
    font-size: 3.5rem;
    font-weight: 700;
    line-height: 1.1;
    margin-bottom: 1.5rem;
    color: var(--color-gray-900);
    
    .highlight {
      background: linear-gradient(
        135deg,
        #FF6B6B 0%,    /* Coral/Salmon */
        #FF8E8E 15%,   /* Light Coral */
        #E85AE8 30%,   /* Magenta */
        #8B5CF6 45%,   /* Purple */
        #6366F1 60%,   /* Indigo */
        #3B82F6 75%,   /* Blue */
        #06B6D4 90%,   /* Cyan */
        #10B981 100%   /* Emerald */
      );
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      display: inline-block;
    }
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
  
  .saas-rise-subtitle {
    font-size: 1.25rem;
    color: var(--color-gray-600);
    margin-bottom: 3rem;
    line-height: 1.6;
  }
}

.saas-rise-features {
  margin-bottom: 3rem;
  
  .feature-item {
    margin-bottom: 2rem;
    
    .feature-content {
      h3 {
        font-size: 1.1rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
        color: var(--color-gray-900);
      }
      
      p {
        color: var(--color-gray-600);
        margin: 0;
      }
    }
  }
}

.saas-rise-stats {
  display: flex;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
  
  .stat {
    display: flex;
    flex-direction: column;
    
    strong {
      font-size: 1.5rem;
      font-weight: 700;
      color: var(--color-indigo-600);
    }
    
    span {
      font-size: 0.9rem;
      color: var(--color-gray-600);
    }
  }
}

.saas-rise-signup {
  position: relative;
  .signup-card {
    position: relative;
    background: var(--color-white);
    border-radius: 1rem;
    padding: 2.5rem;
    max-width: 500px;
    margin: 0 auto;
    
    h2 {
      font-size: 2rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
      color: var(--color-gray-900);
      text-align: center;
    }
    
    > p {
      text-align: center;
      color: var(--color-gray-600);
      margin-bottom: 2rem;
      font-size: 1rem;
    }
    
    .trusted-text {
      text-align: center !important;
      color: var(--color-gray-700) !important;
      margin-top: 1rem !important;
      margin-bottom: 2.5rem !important;
      font-size: 1rem !important;
    }
    
    .trusted-by-saasrise {
      color: #000000 !important;
      font-weight: normal;
      text-decoration: underline !important;
      text-decoration-color: #FF6B6B !important;
      text-decoration-thickness: 2px !important;
      text-underline-offset: 3px !important;
    }

  }
}

.signup-form {
  .form-group {
    margin-bottom: 1.5rem;
    
    label {
      display: block;
      font-weight: 500;
      margin-bottom: 0.5rem;
      color: var(--color-gray-900);
      font-size: 0.95rem;
    }
    
    .form-input {
      width: 100%;
      padding: 0.875rem 1rem;
      border: 1px solid var(--color-gray-300);
      border-radius: 0.5rem;
      font-size: 1rem;
      transition: border-color 0.2s ease;
      background: var(--color-white);
      
      &:focus {
        outline: none;
        border-color: var(--color-indigo-500);
      }
    }
  }
  
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    
    .form-group {
      margin-bottom: 1.5rem;
    }
  }
  
  .password-container {
    position: relative;
    
    .password-toggle {
      position: absolute;
      right: 1rem;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      cursor: pointer;
      color: var(--color-gray-500);
      
      &:hover {
        color: var(--color-gray-700);
      }
    }
  }
  
  .password-requirements {
    font-size: 0.85rem;
    color: var(--color-gray-500);
    margin-top: 0.5rem;
    margin-bottom: 0;
  }
  
  .form-checkboxes {
    margin-bottom: 2rem;
    
    .checkbox-label {
      display: flex;
      align-items: flex-start;
      margin-bottom: 1rem;
      cursor: pointer;
      
      input[type="checkbox"] {
        margin-right: 0.75rem;
        margin-top: 0.125rem;
        flex-shrink: 0;
      }
      
      .checkbox-text {
        font-size: 0.9rem;
        color: var(--color-gray-700);
        line-height: 1.4;
        
        a {
          color: var(--color-indigo-600);
          text-decoration: none;
          
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }
  
  .create-account-btn {
    width: 100%;
    background: var(--color-black);
    color: var(--color-white);
    border: none;
    border-radius: 0.5rem;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: background-color 0.2s ease;
    margin-bottom: 1.5rem;
    
    &:hover {
      background: var(--color-gray-800);
    }
    
    svg {
      flex-shrink: 0;
    }
  }
}

.login-link {
  text-align: center;
  
  p {
    font-size: 0.9rem;
    color: var(--color-gray-600);
    margin: 0;
    
    a {
      color: var(--color-indigo-600);
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.error-message {
  background-color: #FF6B6B;
  color: white;
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  text-align: center;
}

.redirect-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.1);
  z-index: 1000;
  border-radius: 1rem;
}

.success-message {
  text-align: center;
  padding: 2rem 0;
  
  .success-icon {
    margin-bottom: 1.5rem;
    
    svg {
      color: #10B981;
      margin: 0 auto;
      display: block;
    }
  }
  
  h2 {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: var(--color-gray-900);
  }
  
  .success-text {
    font-size: 1.1rem;
    color: var(--color-gray-700);
    margin-bottom: 1rem;
    line-height: 1.6;
    
    strong {
      color: var(--color-gray-900);
    }
  }
  
  .email-reminder {
    font-size: 1rem;
    color: var(--color-gray-600);
    margin-bottom: 2rem;
    line-height: 1.6;
    background: #F3F4F6;
    padding: 1rem;
    border-radius: 0.5rem;
    border-left: 4px solid #10B981;
  }
  
  .login-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background: var(--color-black);
    color: var(--color-white);
    text-decoration: none;
    border-radius: 0.5rem;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
    transition: background-color 0.2s ease;
    
    &:hover {
      background: var(--color-gray-800);
    }
    
    svg {
      flex-shrink: 0;
    }
  }
}
</style> 